import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import api from '../../../api/axiosInstance';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { toStartOfDayISO, toEndOfDayISO } from '../utils/dashboardDateUtils';
import { TestExecutionDashboard, TestExecutionDashboardSummary } from '../interfaces/text-execution-dashboard.interface';


export async function getTestExecutionDashboardSummary(startDate: string, endDate: string): Promise<TestExecutionDashboardSummary[]> {
  const baseUrl = '/api/test-execution-dashboard/summary';
  const startISO = toStartOfDayISO(startDate);
  const endISO = toEndOfDayISO(endDate);
  const url = `${baseUrl}?startDate=${encodeURIComponent(startISO)}&endDate=${encodeURIComponent(endISO)}`;
  const { data } = await api.get(url);
  return (data ?? [])
}

function isToday(dateStr: string): boolean {
  return dateStr === new Date().toISOString().slice(0, 10);
}

export function useTestExecutionDashboardSummary(startDate: string, endDate: string) {
  const { addNotifications } = useSwobNotifications();
  const [hasError, setHasError] = useState(false);
  const endDateIsToday = isToday(endDate);

  const query = useQuery<TestExecutionDashboardSummary[]>({
    queryKey: ['testExecutionsSummary', startDate, endDate],
    queryFn: () => getTestExecutionDashboardSummary(startDate, endDate),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(startDate && endDate),
    // refetches when the user selects today's date every 1 minute
    refetchInterval: !hasError && endDateIsToday ? 600000 : false
  });

  useEffect(() => {
    if (query.error && !hasError) {
      setHasError(true);
      addNotifications([
        {
          title: 'Error - Test Execution Dashboard',
          icon: 'icon-triangle-warning',
          description: 'An error occurred while fetching the test execution status',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  }, [query.error, hasError, addNotifications]);

  return query;
}

export async function getTestExecutionDashboardData(startDate: string, endDate: string): Promise<TestExecutionDashboard[]> {
  const baseUrl = '/api/test-execution-dashboard/executions';
  const startISO = toStartOfDayISO(startDate);
  const endISO = toEndOfDayISO(endDate);
  const url = `${baseUrl}?startDate=${encodeURIComponent(startISO)}&endDate=${encodeURIComponent(endISO)}`;
  const { data } = await api.get(url);
  return (data ?? [])
}

export function useTestExecutionDashboardData(startDate: string, endDate: string) {
  const { addNotifications } = useSwobNotifications();
  const [hasError, setHasError] = useState(false);
  const endDateIsToday = isToday(endDate);

  const query = useQuery<TestExecutionDashboard[]>({
    queryKey: ['testExecutionsByProductLine', startDate, endDate],
    queryFn: () => getTestExecutionDashboardData(startDate, endDate),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(startDate && endDate),
    // refetches when the user selects today's date every 1 minute
    refetchInterval: !hasError && endDateIsToday ? 600000 : false
  });

  useEffect(() => {
    if (query.error && !hasError) {
      setHasError(true);
      addNotifications([
        {
          title: 'Error - Test Execution Dashboard',
          icon: 'icon-triangle-warning',
          description: 'An error occurred while fetching the test execution status',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  }, [query.error, hasError, addNotifications]);

  return query;
}
